import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../api';

// Live voice conversation: listen → send what was said → read the reply aloud → listen again,
// until stopped. onUtterance(text) → Promise<string|null> sends the sentence and resolves with
// the reply to speak.
//
// Two engines:
//  - "server" (default, every browser incl. Brave / Firefox / the desktop app): the mic is
//    recorded as raw PCM, the end of the sentence is detected by volume, the audio goes as WAV to
//    our self-hosted voice service (faster-whisper) and replies are spoken with Piper — through
//    /api/v1/assistant/voice/*.
//  - "browser": the Web Speech API (Chrome / Edge only), used when the voice service is
//    unavailable (e.g. local development without it).

const VOICE_API = `${API_BASE_URL}/api/v1/assistant/voice`;

const SpeechRecognition = typeof window !== 'undefined'
    ? (window.SpeechRecognition || window.webkitSpeechRecognition)
    : null;
const AudioContextClass = typeof window !== 'undefined' ? (window.AudioContext || window.webkitAudioContext) : null;

// Brave (and Chromium builds without Google's speech service) expose webkitSpeechRecognition
// but every session fails with a "network" error.
export const isBrave = typeof navigator !== 'undefined' && !!navigator.brave;
const browserEngineSupported = !!SpeechRecognition && !isBrave && typeof window !== 'undefined' && 'speechSynthesis' in window;
const serverEngineSupported = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && !!AudioContextClass;

export const isVoiceSupported = serverEngineSupported || browserEngineSupported;
export const UNSUPPORTED_VOICE_MESSAGE = 'La conversation vocale nécessite un navigateur récent avec accès au micro.';

// ── Voice activity detection (server engine) ─────────────────────────────────
const SILENCE_TO_END_MS = 900;      // pause that ends the sentence
const MIN_SPEECH_MS = 300;          // shorter bursts are noise (cough, click)
const MAX_UTTERANCE_MS = 20000;     // safety cap on one sentence
const PRE_ROLL_MS = 300;            // audio kept from just before the speech started
const MIN_THRESHOLD = 0.012;        // RMS floor for "speech"

// text the assistant wrote → text to say (units read in full, stray formatting removed) — used by
// the browser engine; the voice service does the same on its side
const toSpeech = (text) => text
    .replace(/[*_#`>|]/g, ' ')
    .replace(/(\d)\s?(dhs?|DH)\b/gi, '$1 dirhams')
    .replace(/\bdhs\b/gi, 'dirhams')
    .replace(/\s+/g, ' ')
    .trim();

// Chrome's synthesis stops long utterances after ~15 s: speak sentence groups of ≤ 200 characters
const toChunks = (text) => {
    const sentences = text.split(/(?<=[.!?:;])\s+/);
    const chunks = [];
    let current = '';
    for (const sentence of sentences) {
        if ((current + ' ' + sentence).trim().length > 200 && current) {
            chunks.push(current.trim());
            current = sentence;
        } else {
            current = `${current} ${sentence}`;
        }
    }
    if (current.trim()) chunks.push(current.trim());
    return chunks;
};

const pickFrenchVoice = () => {
    const voices = window.speechSynthesis.getVoices().filter(v => v.lang?.toLowerCase().startsWith('fr'));
    const score = (v) => (/natural|online|neural/i.test(v.name) ? 4 : 0) + (/google/i.test(v.name) ? 2 : 0) + (v.lang === 'fr-FR' ? 1 : 0);
    return voices.sort((a, b) => score(b) - score(a))[0] || null;
};

// Float32 PCM chunks → 16-bit mono WAV
export const encodeWav = (chunks, sampleRate) => {
    const length = chunks.reduce((n, c) => n + c.length, 0);
    const buffer = new ArrayBuffer(44 + length * 2);
    const view = new DataView(buffer);
    const writeStr = (offset, s) => { for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i)); };
    writeStr(0, 'RIFF'); view.setUint32(4, 36 + length * 2, true); writeStr(8, 'WAVE');
    writeStr(12, 'fmt '); view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, 1, true);
    view.setUint32(24, sampleRate, true); view.setUint32(28, sampleRate * 2, true); view.setUint16(32, 2, true); view.setUint16(34, 16, true);
    writeStr(36, 'data'); view.setUint32(40, length * 2, true);
    let offset = 44;
    for (const chunk of chunks) {
        for (let i = 0; i < chunk.length; i++, offset += 2) {
            const s = Math.max(-1, Math.min(1, chunk[i]));
            view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
        }
    }
    return new Blob([buffer], { type: 'audio/wav' });
};

const isUnavailable = (err) => !err.response || err.response.status === 503 || err.response.status === 502 || err.response.status === 504;

export default function useVoiceConversation({ onUtterance }) {
    const [active, setActive] = useState(false);
    const [phase, setPhase] = useState('idle'); // idle | listening | thinking | speaking
    const [interim, setInterim] = useState('');
    const [level, setLevel] = useState(0);      // mic volume 0..1 while listening (server engine)
    const [error, setError] = useState(null);

    const activeRef = useRef(false);
    const engineRef = useRef(serverEngineSupported ? 'server' : 'browser');
    const onUtteranceRef = useRef(onUtterance);
    useEffect(() => { onUtteranceRef.current = onUtterance; }, [onUtterance]);

    // server engine state
    const audioRef = useRef(null);         // { ctx, stream, source, processor }
    const captureRef = useRef(null);       // current utterance capture state, null when not listening
    const playingRef = useRef(null);       // { audio, url, resolve }
    // browser engine state
    const recognitionRef = useRef(null);
    const voiceRef = useRef(null);

    useEffect(() => {
        if (!browserEngineSupported) return undefined;
        const load = () => { voiceRef.current = pickFrenchVoice(); };
        load();
        window.speechSynthesis.addEventListener('voiceschanged', load);
        return () => window.speechSynthesis.removeEventListener('voiceschanged', load);
    }, []);

    const fail = useCallback((message) => {
        activeRef.current = false;
        setActive(false);
        setPhase('idle');
        setInterim('');
        setError(message);
    }, []);

    // ── turn handling (both engines) ──────────────────────────────────────────
    const handleUtteranceRef = useRef(null);
    const listenRef = useRef(null);

    // ── speaking ──────────────────────────────────────────────────────────────
    const speakBrowser = useCallback((text) => new Promise((resolve) => {
        try {
            const synth = window.speechSynthesis;
            synth.cancel();
            const chunks = toChunks(toSpeech(text));
            if (chunks.length === 0) { resolve(); return; }
            chunks.forEach((chunk, i) => {
                const utterance = new SpeechSynthesisUtterance(chunk);
                utterance.lang = 'fr-FR';
                if (voiceRef.current) utterance.voice = voiceRef.current;
                utterance.rate = 1.05;
                if (i === chunks.length - 1) utterance.onend = () => resolve();
                utterance.onerror = () => resolve(); // cancel() (interrupt / stop) lands here
                synth.speak(utterance);
            });
        } catch {
            resolve();
        }
    }), []);

    const speakServer = useCallback(async (text) => {
        let blob;
        try {
            const res = await axios.post(`${VOICE_API}/speak`, { text }, { withCredentials: true, responseType: 'blob' });
            blob = res.data;
        } catch (err) {
            // voice service down: read this reply with the browser voice when possible
            if (browserEngineSupported) return speakBrowser(text);
            return undefined;
        }
        if (!activeRef.current) return undefined;
        return new Promise((resolve) => {
            const url = URL.createObjectURL(blob);
            const audio = new Audio(url);
            const done = () => {
                URL.revokeObjectURL(url);
                if (playingRef.current?.audio === audio) playingRef.current = null;
                resolve();
            };
            playingRef.current = { audio, resolve: done };
            audio.onended = done;
            audio.onerror = done;
            audio.play().catch(done);
        });
    }, [speakBrowser]);

    const speak = useCallback(async (text) => {
        setPhase('speaking');
        if (engineRef.current === 'server') await speakServer(text);
        else await speakBrowser(text);
    }, [speakServer, speakBrowser]);

    // a sentence was heard: send it, speak the reply, listen again
    handleUtteranceRef.current = async (text) => {
        if (!activeRef.current) return;
        if (!text) { listenRef.current?.(); return; }
        setInterim(text);
        setPhase('thinking');
        let reply = null;
        try {
            reply = await onUtteranceRef.current(text);
        } catch {
            reply = null;
        }
        if (!activeRef.current) return;
        setInterim('');
        if (reply) await speak(reply);
        if (activeRef.current) listenRef.current?.();
    };

    // ── listening: server engine ──────────────────────────────────────────────
    const finishCapture = useCallback(async () => {
        const capture = captureRef.current;
        captureRef.current = null;
        setLevel(0);
        if (!capture || !activeRef.current) return;
        if (capture.speechMs < MIN_SPEECH_MS) { listenRef.current?.(); return; }

        setPhase('thinking');
        setInterim('');
        const wav = encodeWav(capture.chunks, audioRef.current.ctx.sampleRate);
        try {
            const res = await axios.post(`${VOICE_API}/transcribe`, wav, {
                withCredentials: true,
                headers: { 'Content-Type': 'audio/wav' },
            });
            await handleUtteranceRef.current((res.data?.text || '').trim());
        } catch (err) {
            if (!activeRef.current) return;
            if (isUnavailable(err) && browserEngineSupported) {
                // voice service unreachable: carry on with the browser's own recognition
                engineRef.current = 'browser';
                listenRef.current?.();
            } else {
                fail(isUnavailable(err)
                    ? 'Le service vocal est momentanément indisponible.'
                    : "Je n'ai pas pu transcrire l'audio.");
            }
        }
    }, [fail]);

    const startAudio = useCallback(async () => {
        if (audioRef.current) return true;
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
            });
            let ctx;
            try { ctx = new AudioContextClass({ sampleRate: 16000 }); } catch { ctx = new AudioContextClass(); }
            const source = ctx.createMediaStreamSource(stream);
            const processor = ctx.createScriptProcessor(4096, 1, 1);
            const msPerBuffer = (4096 / ctx.sampleRate) * 1000;
            const preRollBuffers = Math.ceil(PRE_ROLL_MS / msPerBuffer);
            let noiseFloor = 0.005;
            const recent = []; // pre-roll ring

            processor.onaudioprocess = (e) => {
                const capture = captureRef.current;
                if (!capture) return; // not listening (thinking / speaking): mic ignored, no echo
                const samples = new Float32Array(e.inputBuffer.getChannelData(0));
                let sum = 0;
                for (let i = 0; i < samples.length; i++) sum += samples[i] * samples[i];
                const rms = Math.sqrt(sum / samples.length);
                const threshold = Math.max(MIN_THRESHOLD, noiseFloor * 3);
                setLevel(Math.min(1, rms / (threshold * 4)));

                if (!capture.started) {
                    recent.push(samples);
                    if (recent.length > preRollBuffers) recent.shift();
                    if (rms > threshold) {
                        capture.started = true;
                        capture.chunks.push(...recent);
                        recent.length = 0;
                        capture.speechMs = msPerBuffer;
                    } else {
                        noiseFloor = noiseFloor * 0.95 + rms * 0.05; // adapt to the room
                    }
                    return;
                }

                capture.chunks.push(samples);
                capture.totalMs += msPerBuffer;
                if (rms > threshold) {
                    capture.speechMs += msPerBuffer;
                    capture.silenceMs = 0;
                } else {
                    capture.silenceMs += msPerBuffer;
                }
                if (capture.silenceMs >= SILENCE_TO_END_MS || capture.totalMs >= MAX_UTTERANCE_MS) {
                    finishCapture();
                }
            };
            source.connect(processor);
            processor.connect(ctx.destination);
            audioRef.current = { ctx, stream, source, processor };
            return true;
        } catch (err) {
            fail(err?.name === 'NotAllowedError'
                ? "Accès au micro refusé. Autorisez le micro dans le navigateur pour parler à l'assistant."
                : "Impossible d'accéder au micro.");
            return false;
        }
    }, [fail, finishCapture]);

    const stopAudio = useCallback(() => {
        const a = audioRef.current;
        audioRef.current = null;
        captureRef.current = null;
        if (!a) return;
        try { a.processor.disconnect(); a.source.disconnect(); } catch { /* already disconnected */ }
        a.stream.getTracks().forEach(t => t.stop());
        a.ctx.close().catch(() => {});
    }, []);

    // ── listening: browser engine ─────────────────────────────────────────────
    const listenBrowser = useCallback(() => {
        const recognition = new SpeechRecognition();
        recognition.lang = 'fr-FR';
        recognition.interimResults = true;
        recognition.continuous = false;
        recognition.maxAlternatives = 1;

        let finalText = '';
        recognition.onresult = (e) => {
            let partial = '';
            for (let i = e.resultIndex; i < e.results.length; i++) {
                const result = e.results[i];
                if (result.isFinal) finalText += result[0].transcript;
                else partial += result[0].transcript;
            }
            setInterim((finalText + ' ' + partial).trim());
        };
        recognition.onerror = (e) => {
            if (e.error === 'no-speech' || e.error === 'aborted') return;
            fail(e.error === 'not-allowed' || e.error === 'service-not-allowed'
                ? "Accès au micro refusé. Autorisez le micro dans le navigateur pour parler à l'assistant."
                : 'La reconnaissance vocale de ce navigateur a rencontré un problème.');
        };
        recognition.onend = () => {
            recognitionRef.current = null;
            if (!activeRef.current) return;
            handleUtteranceRef.current(finalText.trim());
        };
        recognitionRef.current = recognition;
        try { recognition.start(); } catch { /* already started */ }
    }, [fail]);

    listenRef.current = () => {
        if (!activeRef.current) return;
        setPhase('listening');
        setInterim('');
        if (engineRef.current === 'server') {
            captureRef.current = { started: false, chunks: [], speechMs: 0, silenceMs: 0, totalMs: 0 };
        } else {
            listenBrowser();
        }
    };

    // ── controls ──────────────────────────────────────────────────────────────
    const start = useCallback(async () => {
        if (!isVoiceSupported || activeRef.current) return;
        setError(null);
        setInterim('');
        engineRef.current = serverEngineSupported ? 'server' : 'browser';
        activeRef.current = true;
        setActive(true);
        if (engineRef.current === 'server' && !(await startAudio())) return;
        listenRef.current();
    }, [startAudio]);

    const stop = useCallback(() => {
        activeRef.current = false;
        setActive(false);
        setPhase('idle');
        setInterim('');
        setLevel(0);
        stopAudio();
        recognitionRef.current?.abort();
        recognitionRef.current = null;
        playingRef.current?.audio.pause();
        playingRef.current?.resolve();
        if (browserEngineSupported) window.speechSynthesis.cancel();
    }, [stopAudio]);

    // tap on the orb: stop the assistant mid-sentence, or send what was said without waiting
    const interrupt = useCallback(() => {
        if (phase === 'speaking') {
            if (playingRef.current) { playingRef.current.audio.pause(); playingRef.current.resolve(); }
            if (browserEngineSupported) window.speechSynthesis.cancel();
        } else if (phase === 'listening') {
            if (engineRef.current === 'server') {
                if (captureRef.current?.started) finishCapture();
            } else {
                recognitionRef.current?.stop();
            }
        }
    }, [phase, finishCapture]);

    useEffect(() => () => {
        activeRef.current = false;
        stopAudio();
        recognitionRef.current?.abort();
        playingRef.current?.audio.pause();
        if (browserEngineSupported) window.speechSynthesis.cancel();
    }, [stopAudio]);

    return { supported: isVoiceSupported, active, phase, interim, level, error, start, stop, interrupt };
}
